import { useEffect } from 'react';
import { FOCUSABLE_SELECTOR } from './useSpreadsheetNavigation';

const MODAL_SELECTOR = '.ant-modal-wrap';
const OVERLAY_SELECTOR = `.ant-popover:not(.ant-popover-hidden), ${MODAL_SELECTOR}`;
const MAX_FRAMES_TO_WAIT_FOR_OVERLAY = 30;

const isVisible = (element: Element) => window.getComputedStyle(element).display !== 'none';

const getTopmostVisible = (selector: string) => {
	const visible = [...document.querySelectorAll(selector)].filter(isVisible);

	return visible[visible.length - 1];
};

const getFocusable = (element: Element) => (
	element.matches(FOCUSABLE_SELECTOR) ? element : element.querySelector(FOCUSABLE_SELECTOR)
);

const isDisabledTrigger = (element: Element) => (
	element.matches('[disabled], .ant-select-disabled') ||
	element.querySelector('[disabled]') != null ||
	element.closest('button[disabled]') != null
);

const getCell = (element: Element | null) => element?.closest('td') ?? null;

const isInColumn = (element: Element, cell: HTMLTableCellElement) => {
	const elementCell = getCell(element);

	return elementCell?.cellIndex === cell.cellIndex && elementCell.closest('table') === cell.closest('table');
};

const focusInOpenedOverlay = (shortcut: string, framesLeft = MAX_FRAMES_TO_WAIT_FOR_OVERLAY) => {
	const overlay = getTopmostVisible(OVERLAY_SELECTOR);
	const focusable = overlay == null ?
		null :
		getFocusable(overlay.querySelector(`[data-shortcut="${shortcut}"]`) ?? overlay);

	if (focusable instanceof HTMLElement) {
		focusable.focus();

		return;
	}

	if (framesLeft > 0) {
		requestAnimationFrame(() => focusInOpenedOverlay(shortcut, framesLeft - 1));
	}
};

const activate = (target: Element, shortcut: string) => {
	const focusable = getFocusable(target);

	if (focusable instanceof HTMLElement) {
		focusable.focus();

		if (focusable instanceof HTMLInputElement) {
			focusable.select();
		}

		return;
	}

	target.dispatchEvent(new MouseEvent('click', { bubbles: true }));
	focusInOpenedOverlay(shortcut);
};

export const useFocusShortcuts = (shortcutsByKeyCode: Record<string, string>) => {
	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (!event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
				return;
			}

			const shortcut = shortcutsByKeyCode[event.code];

			if (shortcut == null) {
				return;
			}

			const scope = getTopmostVisible(MODAL_SELECTOR) ?? document;
			const targets = [...scope.querySelectorAll(`[data-shortcut="${shortcut}"]`)].filter((element) => (
				getFocusable(element) != null || !isDisabledTrigger(element)
			));
			const activeCell = getCell(document.activeElement);
			const target = (activeCell == null ? undefined : targets.find((t) => isInColumn(t, activeCell))) ?? targets[0];

			if (target == null) {
				return;
			}

			event.preventDefault();
			activate(target, shortcut);
		};

		document.addEventListener('keydown', onKeyDown);

		return () => document.removeEventListener('keydown', onKeyDown);
	}, [shortcutsByKeyCode]);
};
